import { useEffect, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import AdminLayout from '../components/AdminLayout';
import { fetchAdminOrders, updateOrderStatus } from '@/lib/api/orders';
import type { AdminOrderRecord, OrderStatus } from '@/lib/domain';

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-500/20 text-yellow-400',
  confirmed: 'bg-green-500/20 text-green-400',
  shipped: 'bg-sky-500/20 text-sky-400',
};

function OrdersContent() {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const [orders, setOrders] = useState<AdminOrderRecord[]>([]);
  const [filter, setFilter] = useState<'all' | OrderStatus>('all');
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const nextOrders = await fetchAdminOrders();
        setOrders(nextOrders);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to load orders.');
      }
    };

    void load();
  }, []);

  const card = dark ? 'bg-[#1C2B3A] border border-white/5' : 'bg-white border border-gray-200';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';
  const divider = dark ? 'divide-white/5' : 'divide-gray-100';

  const filtered = filter === 'all' ? orders : orders.filter((order) => order.status === filter);

  const handleStatusChange = async (id: string, status: OrderStatus) => {
    setOrders((prev) => prev.map((order) => (order.id === id ? { ...order, status } : order)));

    try {
      await updateOrderStatus(id, status);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update order status.');
    }
  };

  return (
    <div className="space-y-5">
      {error && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {['all', 'pending', 'confirmed', 'shipped'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status as 'all' | OrderStatus)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap capitalize ${
              filter === status
                ? 'text-white'
                : dark
                ? 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'
                : 'bg-gray-100 text-gray-500 hover:text-gray-900 hover:bg-gray-200'
            }`}
            style={filter === status ? { backgroundColor: 'var(--accent)' } : {}}
          >
            {status === 'all' ? 'All Orders' : status}
            <span className="ml-1.5 opacity-70">
              ({status === 'all' ? orders.length : orders.filter((order) => order.status === status).length})
            </span>
          </button>
        ))}
      </div>

      <div className={`${card} rounded-lg overflow-hidden`}>
        <div className={`grid grid-cols-12 gap-3 px-5 py-3 text-xs font-bold uppercase tracking-wide ${muted} border-b ${dark ? 'border-white/10' : 'border-gray-100'}`}>
          <div className="col-span-2">Order ID</div>
          <div className="col-span-3">Dealer</div>
          <div className="col-span-3">Items</div>
          <div className="col-span-1 text-right">Total</div>
          <div className="col-span-2 text-center">Status</div>
          <div className="col-span-1 text-right">Date</div>
        </div>
        <div className={`divide-y ${divider}`}>
          {filtered.length === 0 ? (
            <div className={`px-5 py-10 text-sm ${muted}`}>No orders found.</div>
          ) : (
            filtered.map((order) => (
              <div key={order.id} className={`grid grid-cols-12 gap-3 px-5 py-4 items-center ${dark ? 'hover:bg-white/3' : 'hover:bg-gray-50'} transition-colors`}>
                <div className="col-span-2">
                  <p className={`text-sm font-bold ${text}`}>{order.orderNumber}</p>
                  <p className={`text-xs ${muted}`}>{order.date}</p>
                </div>
                <div className="col-span-3">
                  <p className={`text-sm font-semibold ${text}`}>{order.dealer}</p>
                  <p className={`text-xs ${muted}`}>{order.contact}</p>
                </div>
                <div className="col-span-3">
                  <p className={`text-xs ${muted}`}>{order.items}</p>
                  {order.notes && <p className="text-xs text-yellow-400 mt-0.5 truncate">{order.notes}</p>}
                </div>
                <div className="col-span-1 text-right text-sm font-black" style={{ color: 'var(--accent)' }}>
                  ${order.total.toLocaleString()}
                </div>
                <div className="col-span-2 flex justify-center">
                  <select
                    value={order.status}
                    onChange={(e) => void handleStatusChange(order.id, e.target.value as OrderStatus)}
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full cursor-pointer outline-none border-0 ${statusColors[order.status]} bg-transparent`}
                  >
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="shipped">Shipped</option>
                  </select>
                </div>
                <div className={`col-span-1 text-right text-xs ${muted}`}>{order.date}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminOrdersPage() {
  return (
    <AdminLayout title="Orders">
      <OrdersContent />
    </AdminLayout>
  );
}
